import {
    type FC
} from "react";
import type IDataTableFacetedFilterProps from "./type";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useDataTableContext,
    DataTableContext
} from "../../context";
import {
    ListFilter as ListFilterIcon
} from "lucide-react-native";
import {
    formatNumber
} from "../../../../helpers/locale";
import DataTableFacetOptions from "../../internal/facetOptions";
import Popover from "../../../popover";
import Button from "../../../button";

const DataTableFacetedFilter: FC<IDataTableFacetedFilterProps> = ({
    customTheme: customThemeProp,
    align = "start",
    side = "bottom",
    columnId,
    style,
    title
}) => {
    const context = useDataTableContext({
        part: "FacetedFilter"
    });

    const {
        colors
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const column = context?.allColumns.find((item) => item.id === columnId);

    if(!context || !column?.filterOptions || context.layout === "cards") {
        return null;
    }

    const currentTheme = customThemeProp ?? context.customTheme;

    const currentTitle = title ?? column.header;

    const selectedCount = context.columnFilters[columnId]?.length ?? 0;

    const countText = formatNumber({
        locale: activeLocale,
        value: selectedCount
    });

    const renderIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <ListFilterIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    return <Popover
        customLocalize={context.customLocalize}
        customTheme={currentTheme}
        align={align}
        size="small"
        side={side}
    >
        <Popover.Trigger>
            <Button
                aria-label={selectedCount > 0 ? localize("data-table-filter-selected", [
                    currentTitle,
                    countText
                ]) : currentTitle}
                title={selectedCount > 0 ? localize("data-table-filter-title-count", [
                    currentTitle,
                    countText
                ]) : currentTitle}
                isDisabled={context.isPermissionDenied}
                customTheme={currentTheme}
                onPress={() => undefined}
                icon={renderIcon}
                variant="outline"
                type="neutral"
                style={style}
                size="small"
            />
        </Popover.Trigger>
        <Popover.Content
            aria-label={currentTitle}
            isShowClose={false}
        >
            <DataTableContext.Provider
                value={context}
            >
                <DataTableFacetOptions
                    customTheme={currentTheme}
                    columnId={columnId}
                />
            </DataTableContext.Provider>
            {
                selectedCount > 0 ?
                    <Popover.Footer
                        align="stretch"
                    >
                        <Button
                            onPress={() => context.handleColumnFilterChange(columnId, [])}
                            title={localize("data-table-clear-filter")}
                            variant="ghost"
                            type="neutral"
                        />
                    </Popover.Footer>
                :
                    null
            }
        </Popover.Content>
    </Popover>;
};
export default DataTableFacetedFilter;
